// reports/reportLoaders.ts
//
// "How do I build report X for job Y" — once, for every caller.
//
// Two things need this: the /report routes, and the export buttons on the
// result screens that produce a PDF without navigating anywhere. Keeping the
// fetch-then-build logic here means adding a field to a sheet cannot fix one
// path and miss the other.
//
// Each loader returns null when there is nothing to render — a missing job, or
// one that has not finished analysing — which callers surface as an empty
// state rather than an error.

import {
  getHairAnalysisJob,
  getJob,
  getMakeupAnalysisJob,
  getMe,
  getPremiumJob,
  getSkin,
  getCorrectedJob,
} from '../api/endpoints';
import { resolveReportImages } from './reportImages';
import {
  buildBaseViewModel,
  buildHairViewModel,
  buildMakeupViewModel,
  buildPremiumViewModel,
} from './viewModel';
import type { TemplateContext } from './mustache';
import type { ReportKind } from './templateClient';

export interface LoadedReport {
  viewModel: TemplateContext;
  /** Document title, and the browser's default filename for the saved PDF. */
  title: string;
}

/** Short display id used in a sheet header and the filename. */
function shortId(id: string): string {
  return id.slice(0, 8).toUpperCase();
}

function fullName(user: { name?: string; surname?: string } | null): string | undefined {
  return user?.name ? `${user.name} ${user.surname ?? ''}`.trim() : undefined;
}

/** The poster sheets print a short date inline in the header. */
function posterDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/** The premium and base sheets print a long date in a dedicated card. */
function longDate(iso?: string): string {
  return (iso ? new Date(iso) : new Date()).toLocaleDateString(undefined, { dateStyle: 'long' });
}

async function loadHair(id: string): Promise<LoadedReport | null> {
  const [job, user] = await Promise.all([
    getHairAnalysisJob(id).then(res => res.job),
    getMe().catch(() => null),
  ]);
  if (job.status !== 'done' || !job.result) return null;

  const images = await resolveReportImages({ portrait: job.image_path, atlas: job.result_image_path });
  const title = `kivo-hair-report-${shortId(id)}`;
  return {
    title,
    viewModel: buildHairViewModel(job.result, {
      id: shortId(id),
      date: posterDate(job.submitted_at),
      name: fullName(user),
      title,
    }, images),
  };
}

async function loadMakeup(id: string): Promise<LoadedReport | null> {
  const [job, user] = await Promise.all([
    getMakeupAnalysisJob(id).then(res => res.job),
    getMe().catch(() => null),
  ]);
  if (job.status !== 'done' || !job.result) return null;

  const images = await resolveReportImages({ portrait: job.image_path, atlas: job.result_image_path });
  const title = `kivo-makeup-report-${shortId(id)}`;
  return {
    title,
    viewModel: buildMakeupViewModel(job.result, {
      id: shortId(id),
      date: posterDate(job.submitted_at),
      name: fullName(user),
      title,
    }, images),
  };
}

async function loadPremium(id: string): Promise<LoadedReport | null> {
  const [job, user] = await Promise.all([
    getPremiumJob(id).then(res => res.job),
    getMe().catch(() => null),
  ]);
  if (!job.result) return null;

  const images = await resolveReportImages({ portrait: job.image_path });
  const title = `kivo-premium-report-${shortId(job.id)}`;
  return {
    title,
    viewModel: buildPremiumViewModel(job.result, {
      id: job.id.slice(0, 12).toUpperCase(),
      date: longDate(job.submitted_at),
      name: fullName(user),
      title,
    }, images),
  };
}

async function loadBase(id: string): Promise<LoadedReport | null> {
  const [job, skinData, user] = await Promise.all([
    getJob(id),
    getSkin(id),
    getMe().catch(() => null),
  ]);
  // The typed client promises both, but a deleted or partially-processed job
  // answers with an empty body — the report has nothing to render then.
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!job || !skinData) return null;

  // A user-corrected job supersedes the original analysis; if no correction
  // exists the request 404s and the original result stands.
  let skin = skinData;
  try {
    await getCorrectedJob(id);
    skin = await getSkin(id);
  } catch {
    /* no correction on file — keep the original analysis */
  }

  const images = await resolveReportImages({ portrait: job.image_path });
  const title = `kivo-skin-report-${shortId(job.id)}`;
  return {
    title,
    viewModel: buildBaseViewModel(skin, {
      id: job.id.slice(0, 12).toUpperCase(),
      date: longDate(job.submitted_at),
      name: fullName(user),
      title,
    }, images),
  };
}

const LOADERS: Record<ReportKind, (id: string) => Promise<LoadedReport | null>> = {
  hair: loadHair,
  makeup: loadMakeup,
  premium: loadPremium,
  base: loadBase,
};

/** Fetch everything a sheet needs and build its view-model. */
export function loadReport(kind: ReportKind, id: string): Promise<LoadedReport | null> {
  return LOADERS[kind](id);
}
